import { Component, OnInit, DestroyRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TableModule } from 'primeng/table';
import { Button } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { Select } from 'primeng/select';
import { MultiSelect } from 'primeng/multiselect';
import { InputText } from 'primeng/inputtext';
import { InputNumber } from 'primeng/inputnumber';
import { Checkbox } from 'primeng/checkbox';
import { Tooltip } from 'primeng/tooltip';
import { MessageService, ConfirmationService } from 'primeng/api';
import { forkJoin, catchError, of } from 'rxjs';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import { FutureAllocationService } from '../../services/future-allocation.service';
import { AuthService } from '../../services/auth.service';
import { EmployeeService } from '../../services/employee.service';
import { ProjectService } from '../../services/project.service';
import { RoleService } from '../../services/role.service';
import { CostCenterService } from '../../services/cost-center.service';
import { ServiceGroupService } from '../../services/service-group.service';
import { PageHeaderSearchActionService } from '../../services/page-header-search-action.service';
import { YearMonthFilterService } from '../../services/year-month-filter.service';
import {
  FutureAllocation,
  FutureAllocationRow,
  FutureAllocationSummary,
  BulkCreateFutureAllocation,
  FutureAllocationMonthEntry
} from '../../models/future-allocation.model';
import { InvoiceType, Month } from '../../models/allocation.model';
import { Employee } from '../../models/employee.model';
import { Project } from '../../models/project.model';
import { ServiceGroup } from '../../models/service-group.model';

interface DropdownOption {
  label: string;
  value: number | string;
}

interface WindowMonth {
  key: string;      // "2026-7"
  year: number;
  month: number;    // 1-12
  label: string;    // "Jul"
}

// A grid display row: either a real project row, or a synthetic per-employee "Available" row
// showing remaining capacity per visible month.
type FutureAllocationDisplayRow =
  | { kind: 'project'; row: FutureAllocationRow }
  // Per-employee monthly total across all their projects. Mirrors the Monthly Allocation grid's
  // total row so both pages read the same way.
  | { kind: 'total'; employeeId: string; employeeName: string; totalByKey: Record<string, number> }
  | { kind: 'available'; employeeId: string; availableByKey: Record<string, number> };

// One editable month input in the Add form.
interface FormMonthCell {
  key: string;
  year: number;
  month: number;
  label: string;
  percentage: number | null;
  otherAllocated: number;   // employee's % already allocated to OTHER projects this month
}

@Component({
  selector: 'app-future-allocation',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    Button,
    Dialog,
    ConfirmDialog,
    Select,
    MultiSelect,
    InputText,
    InputNumber,
    Checkbox,
    Tooltip
  ],
  providers: [ConfirmationService],
  templateUrl: './future-allocation.component.html',
  styleUrl: './future-allocation.component.scss'
})
export class FutureAllocationComponent implements OnInit {
  private destroyRef = inject(DestroyRef);

  InvoiceType = InvoiceType;

  // Fixed calendar-year window: always January–December of the selected year.
  // First 6 = Jan–Jun, Next 6 = Jul–Dec.
  readonly windowLength = 12;
  readonly startMonth = 1;  // always January
  selectedYear!: number;
  windowMonths: WindowMonth[] = [];

  // First 6 / Next 6 toggle.
  half: 0 | 1 = 0;

  allocations: FutureAllocation[] = [];
  rows: FutureAllocationRow[] = [];
  filteredRows: FutureAllocationRow[] = [];
  // Grid display rows: project rows interleaved with a synthetic "Available" row per employee
  // (only when that employee has remaining capacity in at least one window month).
  displayRows: FutureAllocationDisplayRow[] = [];
  summaries: FutureAllocationSummary[] = [];

  employees: Employee[] = [];
  projects: Project[] = [];
  serviceGroups: ServiceGroup[] = [];
  roleOptions: DropdownOption[] = [];
  costCenterOptions: DropdownOption[] = [];
  invoiceTypeOptions: DropdownOption[] = [
    { label: 'Monthly', value: InvoiceType.Monthly },
    { label: 'Project Milestone', value: InvoiceType.ProjectMilestone },
    { label: 'Not Available', value: InvoiceType.NotAvailable }
  ];

  loading = true;
  searchText = '';

  // Filters (multi-select) — mirror the Monthly Allocation filter set.
  selectedRoles: string[] = [];
  selectedPMs: string[] = [];
  selectedProjects: string[] = [];
  selectedInvoiceTypes: number[] = [];
  selectedCostCenters: string[] = [];
  selectedServiceGroups: string[] = [];
  showOnlyAvailable = false;

  // Filter options.
  roleFilterOptions: DropdownOption[] = [];
  pmOptions: DropdownOption[] = [];
  projectOptions: DropdownOption[] = [];
  costCenterFilterOptions: DropdownOption[] = [];
  serviceGroupOptions: DropdownOption[] = [];

  // Add/Edit dialog.
  displayDialog = false;
  isEditMode = false;
  editingId: string | null = null;
  // The exact grid line being edited. Needed because duplicate allocations mean (employee, project)
  // no longer identifies a single row — save/reset must act on this row's own months.
  editingRow: FutureAllocationRow | null = null;
  formEmployeeId: string | null = null;
  formRoleId: string | null = null;
  formProjectId: string | null = null;
  formInvoiceType: InvoiceType = InvoiceType.Monthly;
  formCostCenterId: string | null = null;
  formServiceGroupId: string | null = null;
  formComments = '';
  formMonths: FormMonthCell[] = [];
  applyAllValue: number | null = 100;
  // Percentage dropdown presets (editable — users can type over-100 values too).
  percentageOptions: DropdownOption[] = [0, 25, 50, 75, 100].map(v => ({ label: `${v}%`, value: v }));
  // "Available Capacity" info card (shown once an employee is chosen), like the Allocation form.
  showAvailableCard = false;
  // Progressive reveal: enter first 6 months, then optionally reveal the next 6.
  showSecondHalf = false;
  // True when the dialog leads with Jul–Dec (editing a row while the grid is on the Jul–Dec tab).
  leadSecondHalf = false;
  fieldTouched: { [key: string]: boolean } = {};

  // Unallocated dialog — employees with NO allocation anywhere in the window (need assignment).
  displayUnallocatedDialog = false;
  unallocatedEmployees: Employee[] = [];

  constructor(
    private futureAllocationService: FutureAllocationService,
    private employeeService: EmployeeService,
    private projectService: ProjectService,
    private roleService: RoleService,
    private costCenterService: CostCenterService,
    private serviceGroupService: ServiceGroupService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    public pageHeaderService: PageHeaderSearchActionService,
    private yearMonthFilterService: YearMonthFilterService,
    private authService: AuthService
  ) {
    // Year comes from the shared header dropdown (like Forecast).
    this.selectedYear = this.yearMonthFilterService.currentYear;
    // Default the half-year tab to the one containing the current month:
    // Jan–Jun (months 1–6) -> 0, Jul–Dec (months 7–12) -> 1.
    const currentMonth = new Date().getMonth() + 1;
    this.half = currentMonth <= 6 ? 0 : 1;
    this.buildWindow();
  }

  // Sales is read-only: view + export only, no add/edit/delete.
  get canWrite(): boolean {
    return this.authService.hasPMAccess();
  }

  get canExport(): boolean {
    return this.authService.hasPMAccess() || this.authService.hasFinanceAccess() || this.authService.getUser()?.isSales === true;
  }

  ngOnInit(): void {
    this.pageHeaderService.setCanShowFilterToggle(true);
    this.loadLookups();
    // React to the header Year dropdown.
    this.yearMonthFilterService.year
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(year => {
        if (this.selectedYear !== year) {
          this.selectedYear = year;
          this.buildWindow();
          this.loadAllocations();
        } else {
          this.loadAllocations();
        }
      });
  }

  // ---------- window helpers ----------
  // Jan–Dec of the selected year (fixed calendar year).
  private buildWindow(): void {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    this.windowMonths = [];
    for (let month = 1; month <= this.windowLength; month++) {
      this.windowMonths.push({
        key: `${this.selectedYear}-${month}`,
        year: this.selectedYear,
        month,
        label: monthNames[month - 1]
      });
    }
  }

  get visibleMonths(): WindowMonth[] {
    return this.half === 0 ? this.windowMonths.slice(0, 6) : this.windowMonths.slice(6, 12);
  }

  setHalf(half: 0 | 1): void {
    this.half = half;
  }

  // ---------- data loading ----------
  private loadLookups(): void {
    forkJoin({
      employees: this.employeeService.getAllEmployees().pipe(catchError(() => of([] as Employee[]))),
      projects: this.projectService.getAllProjects(true).pipe(catchError(() => of([] as Project[]))),
      roles: this.roleService.getAllRoles().pipe(catchError(() => of([] as any[]))),
      costCenters: this.costCenterService.getAllCostCenters().pipe(catchError(() => of([] as any[]))),
      serviceGroups: this.serviceGroupService.getAllServiceGroups().pipe(catchError(() => of([] as ServiceGroup[])))
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data) => {
        // Future Allocation has its own exclusion flag. It used to filter on
        // excludeFromForecast, so excluding someone from the revenue forecast quietly
        // removed them from capacity planning too.
        this.employees = (data.employees || [])
          .filter(e => e.isActive && !e.excludeFromFutureAllocation);
        // includePresales: true — in-pursuit deals are staffable from Lead. Badge them so the
        // planner can tell a pursuit apart from committed delivery work at a glance.
        this.projects = (data.projects || [])
          .filter(p => p.isActive)
          .map(p => ({ ...p, name: p.isPresales ? `${p.name} (Presales)` : p.name }));
        this.serviceGroups = (data.serviceGroups || []).filter(sg => sg.isActive);
        this.roleOptions = (data.roles || [])
          .filter((r: any) => r.isActive !== false)
          .map((r: any) => ({ label: r.title ?? r.name, value: r.id }));
        // Badge presales-only cost centers, mirroring the project dropdown.
        this.costCenterOptions = (data.costCenters || [])
          .filter((c: any) => c.isActive !== false)
          .map((c: any) => ({
            label: c.isPresales ? `${c.name} (Presales)` : c.name,
            value: c.id
          }));
        this.projectOptions = this.projects.map(p => ({ label: p.name, value: p.id }));
        this.serviceGroupOptions = this.serviceGroups.map(sg => ({ label: sg.name, value: sg.id }));

        // Filter option sets (mirror Monthly Allocation).
        this.roleFilterOptions = [...this.roleOptions];
        this.costCenterFilterOptions = [...this.costCenterOptions];
        // PM list = distinct project manager names across active projects.
        const pmNames = [...new Set(this.projects.map(p => p.managerName).filter((n): n is string => !!n))].sort();
        this.pmOptions = pmNames.map(n => ({ label: n, value: n }));
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load reference data.' });
      }
    });
  }

  private loadAllocations(): void {
    this.loading = true;
    forkJoin({
      allocations: this.futureAllocationService.getByWindow(this.selectedYear, this.startMonth, this.windowLength)
        .pipe(catchError(() => of([] as FutureAllocation[]))),
      summaries: this.futureAllocationService.getSummary(this.selectedYear, this.startMonth, this.windowLength)
        .pipe(catchError(() => of([] as FutureAllocationSummary[])))
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data) => {
        this.allocations = data.allocations || [];
        this.summaries = data.summaries || [];
        this.buildRows();
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load future allocations.' });
      }
    });
  }

  // ---------- grid transformation ----------
  // Identity of one grid line. Duplicate allocations on the same project in the same month are
  // allowed, so grouping on projectId alone would collapse them into one row and overwrite the
  // percentages. Invoice type / cost center / service group are what actually distinguish two
  // allocations on the same project, so they form the key.
  private rowGroupKey(a: FutureAllocation): string {
    return [a.projectId, a.invoiceType, a.costCenterId ?? '', a.serviceGroupId ?? ''].join('|');
  }

  private buildRows(): void {
    // Group by employee, then by allocation identity → one row per saved allocation, each with a
    // %/month map.
    const byEmployee = new Map<string, Map<string, FutureAllocationRow[]>>();

    for (const a of this.allocations) {
      let keyMap = byEmployee.get(a.employeeId);
      if (!keyMap) {
        keyMap = new Map<string, FutureAllocationRow[]>();
        byEmployee.set(a.employeeId, keyMap);
      }

      const key = this.rowGroupKey(a);
      let bucket = keyMap.get(key);
      if (!bucket) {
        bucket = [];
        keyMap.set(key, bucket);
      }

      const monthKey = `${a.year}-${a.month}`;
      // Two allocations can be identical on every grouping field AND land in the same month. Give
      // each its own row rather than letting the later one overwrite the earlier — otherwise a
      // saved allocation silently vanishes from the grid.
      let row = bucket.find(r => !(monthKey in r.byMonth));
      if (!row) {
        row = {
          groupKey: bucket.length === 0 ? key : `${key}#${bucket.length}`,
          employeeId: a.employeeId,
          employeeName: a.employeeName,
          roleId: a.roleId,
          roleTitle: a.roleTitle,
          projectId: a.projectId,
          projectName: a.projectName,
          isProjectPresales: a.isProjectPresales,
          managerName: a.managerName,
          invoiceType: a.invoiceType,
          costCenterId: a.costCenterId,
          costCenterName: a.costCenterName,
          serviceGroupId: a.serviceGroupId,
          serviceGroupName: a.serviceGroupName,
          byMonth: {},
          isFirstInGroup: false,
          groupSize: 0
        };
        bucket.push(row);
      }
      row.byMonth[monthKey] = { id: a.id, percentage: a.percentage };
    }

    const rows: FutureAllocationRow[] = [];
    for (const keyMap of byEmployee.values()) {
      const projectRows = Array.from(keyMap.values())
        .flat()
        .sort((x, y) => x.projectName.localeCompare(y.projectName) || x.groupKey.localeCompare(y.groupKey));
      projectRows.forEach((r, idx) => {
        r.isFirstInGroup = idx === 0;
        r.groupSize = projectRows.length;
        rows.push(r);
      });
    }

    rows.sort((x, y) => x.employeeName.localeCompare(y.employeeName));
    this.rows = rows;
    this.applyFilters();
  }

  applyFilters(): void {
    const search = this.searchText.trim().toLowerCase();
    this.filteredRows = this.rows.filter(r => {
      if (this.selectedRoles.length && !(r.roleId && this.selectedRoles.includes(r.roleId))) {
        return false;
      }
      if (this.selectedPMs.length && !(r.managerName && this.selectedPMs.includes(r.managerName))) {
        return false;
      }
      if (this.selectedProjects.length && !this.selectedProjects.includes(r.projectId)) {
        return false;
      }
      if (this.selectedInvoiceTypes.length && !this.selectedInvoiceTypes.includes(r.invoiceType)) {
        return false;
      }
      if (this.selectedCostCenters.length && !(r.costCenterId && this.selectedCostCenters.includes(r.costCenterId))) {
        return false;
      }
      if (this.selectedServiceGroups.length && !(r.serviceGroupId && this.selectedServiceGroups.includes(r.serviceGroupId))) {
        return false;
      }
      // Has Availability: keep only employees with remaining capacity in some window month.
      if (this.showOnlyAvailable) {
        const hasFree = this.windowMonths.some(m => this.getEmployeeMonthTotal(r.employeeId, m) < 100);
        if (!hasFree) {
          return false;
        }
      }
      if (search && !(`${r.employeeName} ${r.projectName}`.toLowerCase().includes(search))) {
        return false;
      }
      return true;
    });
    this.buildDisplayRows();
  }

  // Interleave a synthetic "Available" row after each employee's project rows,
  // but ONLY when that employee has remaining capacity (>0) in some window month.
  private buildDisplayRows(): void {
    const out: FutureAllocationDisplayRow[] = [];
    const seenEmployees = new Set<string>();

    for (const row of this.filteredRows) {
      out.push({ kind: 'project', row });

      // When we reach the last visible project row of an employee, append the Available row.
      const empRows = this.filteredRows.filter(r => r.employeeId === row.employeeId);
      const isLastOfEmployee = empRows[empRows.length - 1] === row;
      if (isLastOfEmployee && !seenEmployees.has(row.employeeId)) {
        seenEmployees.add(row.employeeId);

        const availableByKey: Record<string, number> = {};
        let anyRemaining = false;
        for (const m of this.windowMonths) {
          const remaining = Math.max(0, 100 - this.getEmployeeMonthTotal(row.employeeId, m));
          availableByKey[m.key] = remaining;
          if (remaining > 0) {
            anyRemaining = true;
          }
        }

        // Only show the row if there is remaining capacity in at least one month.
        if (anyRemaining) {
          out.push({ kind: 'available', employeeId: row.employeeId, availableByKey });
        }

        // Total goes LAST, closing the employee's block — same order as the Monthly Allocation grid
        // (projects, then Available, then "<Employee> — Total"). Always shown: a 0% or over-allocated
        // total is exactly when the row matters most.
        const totalByKey: Record<string, number> = {};
        for (const m of this.windowMonths) {
          totalByKey[m.key] = Math.round(this.getEmployeeMonthTotal(row.employeeId, m));
        }
        out.push({
          kind: 'total',
          employeeId: row.employeeId,
          employeeName: row.employeeName,
          totalByKey
        });
      }
    }

    this.displayRows = out;
  }

  getAvailableForMonth(availableByKey: Record<string, number>, m: WindowMonth): number {
    return availableByKey[m.key] ?? 0;
  }

  getTotalForMonth(totalByKey: Record<string, number>, m: WindowMonth): number {
    return totalByKey[m.key] ?? 0;
  }

  // Sub-line under the employee name: "role · cost centre · service group".
  // Same shape as the Monthly Allocation grid's roleLabel so both pages read identically.
  employeeSubLabel(row: FutureAllocationRow): string {
    return [row.roleTitle, row.costCenterName, row.serviceGroupName]
      .filter(Boolean)
      .join(' · ') || '—';
  }

  onSearch(value: string): void {
    this.searchText = value;
    this.applyFilters();
  }

  clearFilters(): void {
    this.selectedRoles = [];
    this.selectedPMs = [];
    this.selectedProjects = [];
    this.selectedInvoiceTypes = [];
    this.selectedCostCenters = [];
    this.selectedServiceGroups = [];
    this.showOnlyAvailable = false;
    this.searchText = '';
    this.applyFilters();
  }

  hasActiveFilters(): boolean {
    return this.selectedRoles.length > 0
      || this.selectedPMs.length > 0
      || this.selectedProjects.length > 0
      || this.selectedInvoiceTypes.length > 0
      || this.selectedCostCenters.length > 0
      || this.selectedServiceGroups.length > 0
      || this.showOnlyAvailable
      || this.searchText.trim().length > 0;
  }

  // ---------- row/cell display ----------
  getCellPercent(row: FutureAllocationRow, m: WindowMonth): number {
    return row.byMonth[m.key]?.percentage ?? 0;
  }

  // Severity class for a month cell. MUST stay identical to Monthly Allocation's
  // getWeekPercentSeverity (allocation-grid.component.ts) so the same number is the same colour on
  // both pages: null/'' → empty (muted), 0 → zero (amber), exactly 50 → half (green),
  // exactly 100 → full (primary), >100 → over (amber), anything else → normal (primary).
  // Previously this returned 'empty' for 0 (grey, not amber) and 'half' for anything >=50, so 75%
  // showed green here and primary on the Allocation page.
  getWeekPercentSeverity(pct: number | string | null | undefined): string {
    if (pct == null || pct === '') return 'empty';
    const n = Number(pct);
    if (Number.isNaN(n)) return 'empty';
    if (n === 0) return 'zero';
    if (n === 50) return 'half';
    if (n === 100) return 'full';
    if (n > 100) return 'over';
    return 'normal';
  }

  getEmployeeMonthTotal(employeeId: string, m: WindowMonth): number {
    return this.rows
      .filter(r => r.employeeId === employeeId)
      .reduce((sum, r) => sum + (r.byMonth[m.key]?.percentage ?? 0), 0);
  }

  getEmployeeRows(employeeId: string): FutureAllocationRow[] {
    return this.rows.filter(r => r.employeeId === employeeId);
  }

  getInvoiceTypeLabel(type: InvoiceType): string {
    switch (type) {
      case InvoiceType.Monthly: return 'Monthly';
      case InvoiceType.ProjectMilestone: return 'Milestone';
      case InvoiceType.NotAvailable: return 'N/A';
      default: return '-';
    }
  }

  getInvoiceTypeSeverity(type: InvoiceType): string {
    switch (type) {
      case InvoiceType.Monthly: return 'success';
      case InvoiceType.ProjectMilestone: return 'info';
      case InvoiceType.NotAvailable: return 'neutral';
      default: return 'neutral';
    }
  }

  getInitials(name: string): string {
    return name.split(' ').filter(Boolean).map(w => w[0]).join('').substring(0, 2).toUpperCase();
  }

  // ---------- stat cards ----------
  get avgUtilization(): number {
    if (!this.summaries.length) {
      return 0;
    }
    const withData = this.summaries.filter(s => s.averageUtilization > 0);
    if (!withData.length) {
      return 0;
    }
    return Math.round(withData.reduce((s, x) => s + x.averageUtilization, 0) / withData.length);
  }

  get overAllocatedCount(): number {
    return this.summaries.reduce((s, x) => s + x.overAllocatedCount, 0);
  }

  get fullyBookedCount(): number {
    const employeeIds = [...new Set(this.rows.map(r => r.employeeId))];
    return employeeIds.filter(id =>
      this.windowMonths.every(m => this.getEmployeeMonthTotal(id, m) >= 100)).length;
  }

  get underUtilizedCount(): number {
    const employeeIds = [...new Set(this.rows.map(r => r.employeeId))];
    return employeeIds.filter(id =>
      this.windowMonths.some(m => {
        const t = this.getEmployeeMonthTotal(id, m);
        return t > 0 && t < 50;
      })).length;
  }

  // Count of employees with NO future allocation anywhere in the window — shown on the Unallocated button.
  get unallocatedCount(): number {
    const allocatedIds = new Set(this.rows.map(r => r.employeeId));
    return this.employees.filter(e => !allocatedIds.has(e.id)).length;
  }

  // ---------- Add / Edit dialog ----------
  openAddDialog(): void {
    this.isEditMode = false;
    this.editingId = null;
    this.editingRow = null;
    this.formEmployeeId = null;
    this.formRoleId = null;
    this.formProjectId = null;
    this.formInvoiceType = InvoiceType.Monthly;
    this.formCostCenterId = null;
    this.formServiceGroupId = null;
    this.formComments = '';
    this.applyAllValue = 100;
    // All 12 months visible by default (2026-08-13). The form previously opened on the first 6 with
    // an "Add next 6 months" reveal; planning a full year meant an extra click every time.
    this.showSecondHalf = true;
    // Adding always starts at the top of the year, whichever half the grid is showing.
    this.leadSecondHalf = false;
    this.fieldTouched = {};
    this.showAvailableCard = false;
    this.buildFormMonths(null, null);
    this.displayDialog = true;
  }

  openEditDialog(row: FutureAllocationRow): void {
    this.isEditMode = true;
    this.editingId = null; // bulk-style edit of one project across months
    this.editingRow = row;
    this.formEmployeeId = row.employeeId;
    this.formProjectId = row.projectId;
    this.formInvoiceType = row.invoiceType;
    this.formCostCenterId = row.costCenterId ?? null;
    this.formServiceGroupId = row.serviceGroupId ?? null;
    this.formComments = '';
    this.applyAllValue = 100;
    this.fieldTouched = {};
    // Lead with the half the PM is looking at, so the inputs in front of them are the months they
    // just clicked. Without this they edit Jan–Jun believing it's Jul–Dec.
    this.leadSecondHalf = this.half === 1;
    this.buildFormMonths(row.employeeId, row);
    this.showAvailableCard = true;
    // Always show all 12 months on edit — hiding half an existing allocation behind a reveal button
    // was how the "edit doesn't save" data loss happened (the hidden half got deleted on save).
    this.showSecondHalf = true;
    this.displayDialog = true;
  }

  // The always-visible block. Normally Jan–Jun, but when editing a row from the Jul–Dec grid tab it
  // shows Jul–Dec instead: the PM clicked a Jul–Dec value, so those must be the inputs in front of
  // them. Progressive entry (first 6, then reveal) is kept — only which half leads changes.
  get formMonthsFirstHalf(): FormMonthCell[] {
    return this.leadSecondHalf ? this.formMonths.slice(6, 12) : this.formMonths.slice(0, 6);
  }

  // The second block of 6. Both blocks are always rendered now (all 12 months visible), so this is
  // purely a layout split — there is no reveal step any more.
  get formMonthsSecondHalf(): FormMonthCell[] {
    return this.leadSecondHalf ? this.formMonths.slice(0, 6) : this.formMonths.slice(6, 12);
  }

  // editingRow is the exact grid line being edited (null when adding). It must be the row itself,
  // not a re-lookup by (employee, project): with duplicate allocations allowed, several rows share
  // that pair and a lookup would load the wrong one's percentages.
  private buildFormMonths(employeeId: string | null, editingRow: FutureAllocationRow | null): void {
    this.formMonths = this.windowMonths.map(m => {
      const thisProjectPct = editingRow?.byMonth[m.key]?.percentage ?? null;
      const total = employeeId ? this.getEmployeeMonthTotal(employeeId, m) : 0;
      const other = total - (thisProjectPct ?? 0);
      return {
        key: m.key,
        year: m.year,
        month: m.month,
        label: `${m.label} ${m.year}`,
        percentage: thisProjectPct,
        otherAllocated: other
      };
    });
  }

  applyAllMonths(): void {
    const v = this.applyAllValue ?? 0;
    // Only fill the months currently shown, so revealing the second half later stays intentional.
    const target = this.showSecondHalf ? this.formMonths : this.formMonthsFirstHalf;
    target.forEach(fm => fm.percentage = v);

    // Bulk-filling is the easiest way to push several months over at once, so warn here too —
    // one toast naming the affected months rather than one per month.
    const over = target.filter(fm => fm.otherAllocated + v > 100);
    if (over.length) {
      this.lastOverToastKey = null;
      this.messageService.add({
        severity: 'warn',
        summary: 'Over-Allocation Warning',
        detail: over.length === 1
          ? `${over[0].label} exceeds available capacity (${(over[0].otherAllocated + v).toFixed(1)}%).`
          : `${over.length} months exceed available capacity at ${v}%.`,
        life: 4000,
        styleClass: 'toast-over-allocated'
      });
    }
  }

  // Editable p-select can yield a string — coerce to a number everywhere it's used.
  private pct(fm: FormMonthCell): number {
    const n = Number(fm.percentage);
    return Number.isFinite(n) ? n : 0;
  }

  monthRemaining(fm: FormMonthCell): number {
    return 100 - fm.otherAllocated - this.pct(fm);
  }

  monthIsOver(fm: FormMonthCell): boolean {
    return this.monthRemaining(fm) < 0;
  }

  // Capacity still free for THIS project in a month (100 - other projects - what's already typed here).
  monthAvailable(fm: FormMonthCell): number {
    return Math.max(0, 100 - fm.otherAllocated - this.pct(fm));
  }

  // Warn as soon as a month is pushed over 100%, matching the Allocation form's behaviour. The save
  // is still allowed — this is advisory, which is why it's a toast rather than a blocking error.
  //
  // Takes the emitted value rather than reading fm.percentage: the editable p-select fires
  // ngModelChange before the bound property is updated, so reading it here sees the OLD value and
  // the warning lags one keystroke behind (or never fires on the first entry).
  // Only one over-allocation toast at a time — keyed by month so retyping the same month replaces
  // its warning instead of stacking a new one.
  private lastOverToastKey: string | null = null;

  onMonthPercentageChange(fm: FormMonthCell, value?: unknown): void {
    const entered = value === undefined ? this.pct(fm) : (Number(value) || 0);
    const total = fm.otherAllocated + entered;

    if (total <= 100) {
      this.lastOverToastKey = null;
      return;
    }

    // Don't re-fire while the same month stays over — the editable select emits on every keystroke.
    if (this.lastOverToastKey === fm.key) {
      return;
    }
    this.lastOverToastKey = fm.key;

    // NOTE: no messageService.clear() here. clear() queues a clear command that the toast can
    // process after this add(), swallowing the message — which is why this warning never appeared.
    this.messageService.add({
      severity: 'warn',
      summary: 'Over-Allocation Warning',
      detail: `${fm.label} allocation exceeds available capacity. Total allocation will be ${total.toFixed(1)}%.`,
      life: 4000,
      styleClass: 'toast-over-allocated'
    });
  }

  // "Use available" for one month: add the remaining capacity to this month's value (like the Allocation form).
  useAvailableForMonth(fm: FormMonthCell): void {
    const available = 100 - fm.otherAllocated - this.pct(fm);
    if (available <= 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'No capacity',
        detail: `${fm.label} has no remaining capacity.`
      });
      return;
    }
    fm.percentage = this.pct(fm) + available;
  }

  get canSaveForm(): boolean {
    // Over-allocation (>100%) is allowed — we only warn. Require employee, project and at least one month.
    return !!this.formEmployeeId && !!this.formProjectId
      && this.formMonths.some(fm => this.pct(fm) > 0);
  }

  // Role dropdown filtered to the selected employee's assigned roles (employees can have several).
  // Falls back to all roles when no employee is chosen or the employee has none assigned.
  get allocationRoleOptions(): DropdownOption[] {
    if (!this.formEmployeeId) {
      return this.roleOptions;
    }
    const emp = this.employees.find(e => e.id === this.formEmployeeId);
    if (!emp) {
      return this.roleOptions;
    }
    if (emp.roleIds && emp.roleIds.length > 0) {
      return this.roleOptions.filter(o => emp.roleIds!.includes(o.value as string));
    }
    return this.roleOptions;
  }

  // Employee chosen → auto-fill role (first assigned, still editable) + default service group.
  onFormEmployeeChange(): void {
    const emp = this.employees.find(e => e.id === this.formEmployeeId);
    if (emp) {
      const assignedRoleIds = (emp.roleIds && emp.roleIds.length > 0)
        ? emp.roleIds
        : (emp.roleId ? [emp.roleId] : []);
      const primaryRoleId = assignedRoleIds.length > 0 ? assignedRoleIds[0] : emp.roleId;
      if (primaryRoleId) {
        this.formRoleId = primaryRoleId;
      }
      if (emp.serviceGroupId) {
        this.formServiceGroupId = emp.serviceGroupId;
      }
    }
    this.showAvailableCard = !!this.formEmployeeId;
    this.buildFormMonths(this.formEmployeeId, this.editingRow);
  }

  // Project chosen → auto-fill invoice type + cost center (editable) and service group (locked).
  onFormProjectChange(): void {
    const project = this.projects.find(p => p.id === this.formProjectId);
    if (project) {
      if (project.invoiceType !== undefined && project.invoiceType !== null) {
        this.formInvoiceType = project.invoiceType;
      }
      this.formCostCenterId = project.costCenterId ?? null;
      // Service Group comes from the project and is not user-editable (matches Monthly Allocation).
      if (project.serviceGroupId) {
        this.formServiceGroupId = project.serviceGroupId;
      }
    }
    this.buildFormMonths(this.formEmployeeId, this.editingRow);
  }

  // Available capacity for a month = 100 − other projects − what's typed here (for the info card).
  monthCardAvailable(fm: FormMonthCell): number {
    return 100 - fm.otherAllocated - (fm.percentage ?? 0);
  }

  // "Fill with Available" — set every shown month to its remaining capacity (like the Allocation card button).
  fillAllWithAvailable(): void {
    const target = this.showSecondHalf ? this.formMonths : this.formMonthsFirstHalf;
    target.forEach(fm => {
      const available = 100 - fm.otherAllocated;
      fm.percentage = Math.max(0, available);
    });
  }

  // Reset the month percentages back to their loaded/empty state.
  resetForm(): void {
    this.buildFormMonths(this.formEmployeeId, this.editingRow);
  }

  saveForm(): void {
    if (!this.canSaveForm || !this.formEmployeeId || !this.formProjectId) {
      return;
    }

    // Only months the PM could actually see and change are part of this save. A collapsed half is
    // untouched, so its stored rows must survive — including them in replaceIds below would delete
    // them while `months` (non-zero only) can't recreate them.
    // NOTE: formMonthsFirstHalf is the LEADING half, which is Jul–Dec when leadSecondHalf is set —
    // so this stays correct regardless of which half the dialog opened on.
    const editedMonths = this.showSecondHalf
      ? this.formMonths
      : this.formMonthsFirstHalf;

    const months: FutureAllocationMonthEntry[] = editedMonths
      .filter(fm => this.pct(fm) > 0)
      .map(fm => ({ year: fm.year, month: fm.month as Month, percentage: this.pct(fm) }));

    const dto: BulkCreateFutureAllocation = {
      employeeId: this.formEmployeeId,
      roleId: this.formRoleId ?? undefined,
      projectId: this.formProjectId,
      invoiceType: this.formInvoiceType,
      costCenterId: this.formCostCenterId ?? undefined,
      serviceGroupId: this.formServiceGroupId ?? undefined,
      comments: this.formComments || undefined,
      // On edit, replace only THIS row's rows for the months being submitted. Scoping by id keeps a
      // sibling allocation on the same project intact; scoping to editedMonths means clearing a
      // month to 0 deletes it (it's in replaceIds, absent from months) while a hidden month is
      // neither deleted nor recreated.
      replace: this.isEditMode,
      replaceIds: this.isEditMode && this.editingRow
        ? editedMonths
            .map(fm => this.editingRow!.byMonth[fm.key]?.id)
            .filter((id): id is string => !!id)
        : undefined,
      months
    };

    this.futureAllocationService.createBulk(dto)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Future allocation saved.' });
          this.displayDialog = false;
          this.loadAllocations();
        },
        error: (err) => {
          this.messageService.add({ severity: 'error', summary: 'Could not save', detail: err.message });
        }
      });
  }

  // ---------- delete ----------
  deleteRow(row: FutureAllocationRow, event: Event): void {
    const ids = Object.values(row.byMonth).map(v => v.id);
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: `Remove all ${ids.length} monthly allocations for ${row.employeeName} on ${row.projectName}?`,
      header: 'Confirm removal',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        forkJoin(ids.map(id => this.futureAllocationService.delete(id).pipe(catchError(() => of(null)))))
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.messageService.add({ severity: 'success', summary: 'Removed', detail: 'Allocation removed.' });
              this.loadAllocations();
            }
          });
      }
    });
  }

  // ---------- unallocated ----------
  // Employees with NO future allocation anywhere in the window — they need assignment.
  openUnallocatedDialog(): void {
    const allocatedIds = new Set(this.rows.map(r => r.employeeId));
    this.unallocatedEmployees = this.employees
      .filter(e => !allocatedIds.has(e.id))
      .sort((a, b) => a.name.localeCompare(b.name));
    this.displayUnallocatedDialog = true;
  }

  hideUnallocatedDialog(): void {
    this.displayUnallocatedDialog = false;
  }

  getRoleName(roleId?: string): string {
    if (!roleId) {
      return '-';
    }
    return (this.roleOptions.find(o => o.value === roleId)?.label as string) ?? '-';
  }

  getServiceGroupName(serviceGroupId?: string): string {
    if (!serviceGroupId) {
      return '-';
    }
    return (this.serviceGroupOptions.find(o => o.value === serviceGroupId)?.label as string) ?? '-';
  }

  // Open the Add form pre-filled for a specific unallocated employee.
  openAddAllocationForEmployee(employee: Employee): void {
    this.displayUnallocatedDialog = false;
    this.openAddDialog();
    this.formEmployeeId = employee.id;
    this.onFormEmployeeChange();
  }

  // Row quick-add: open the Add dialog pre-filled with this row's employee.
  quickAddForRow(row: FutureAllocationRow, event?: Event): void {
    event?.stopPropagation();
    this.openAddDialog();
    this.formEmployeeId = row.employeeId;
    this.onFormEmployeeChange();
  }

  // Export the unallocated employee list client-side (xlsx), like the Allocation page.
  exportUnallocatedToExcel(): void {
    if (!this.unallocatedEmployees.length) {
      this.messageService.add({ severity: 'warn', summary: 'Nothing to export', detail: 'No unallocated employees.' });
      return;
    }
    const data = this.unallocatedEmployees.map(e => ({
      'Employee Name': e.name,
      'Email': e.email || '-',
      'Role': this.getRoleName(e.roleId),
      'Designation': e.designationName || '-',
      'Category': e.categoryName || '-',
      'Service Group': e.serviceGroupName || this.getServiceGroupName(e.serviceGroupId)
    }));
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Unallocated');
    XLSX.writeFile(workbook, `Future_Allocation_Unallocated_${this.selectedYear}.xlsx`);
  }

  // ---------- export ----------
  exportToExcel(): void {
    if (!this.rows.length) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Nothing to export',
        detail: 'There are no future allocations in this window yet.'
      });
      return;
    }

    this.messageService.add({ severity: 'info', summary: 'Preparing export', detail: 'Generating Excel file…' });

    this.futureAllocationService.exportToExcel(this.selectedYear, this.startMonth, this.windowLength)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (blob) => {
          const monthName = this.windowMonths[0]?.label ?? this.startMonth;
          saveAs(blob, `Future_Allocation_${monthName}_${this.selectedYear}.xlsx`);
          this.messageService.add({
            severity: 'success',
            summary: 'Export ready',
            detail: 'Your Excel file has been downloaded.'
          });
        },
        error: (err) => {
          this.messageService.add({ severity: 'error', summary: 'Export failed', detail: err.message });
        }
      });
  }
}
